import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Chip, Pagination, Tooltip
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

const EVENT_TYPES = ["Annual", "Holiday", "Semester", "Other"];

const TYPE_COLORS = {
  Annual: { bg: "#EEF2FF", color: "#244C77" },
  Holiday: { bg: "#FEF2F2", color: "#EF4444" },
  Semester: { bg: "#F0FDF4", color: "#16A34A" },
  Other: { bg: "#FFF7ED", color: "#EA580C" },
  annual: { bg: "#EEF2FF", color: "#244C77" },
  holiday: { bg: "#FEF2F2", color: "#EF4444" },
  semester: { bg: "#F0FDF4", color: "#16A34A" },
  other: { bg: "#FFF7ED", color: "#EA580C" },
};

const FALLBACK = [
  { _id: "1", title: "Annual Calendar 2025-2026", startDate: "2025-08-01", endDate: "2026-05-31", eventType: "Annual", description: "Full academic year calendar" },
  { _id: "2", title: "Summer Holiday", startDate: "2026-06-01", endDate: "2026-07-31", eventType: "Holiday", description: "Summer break" },
  { _id: "3", title: "Spring Break", startDate: "2026-03-15", endDate: "2026-03-22", eventType: "Holiday", description: "Spring break week" },
];

const emptyForm = { title: "", startDate: "", endDate: "", eventType: "Annual", description: "" };

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>Are you sure you want to delete this calendar event?</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

const AdminCalendarConfig = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/calendar-events/list");
      setRows(Array.isArray(res?.data) ? res.data : FALLBACK);
    } catch { setRows(FALLBACK); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openAdd = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({
      title: row.title || "", startDate: row.startDate || "",
      endDate: row.endDate || "", eventType: row.eventType || "Annual",
      description: row.description || "",
    });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditId(null); };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("error", "Title is required."); return; }
    try {
      if (editId) {
        await putData(`admin/calendar-events/update/${editId}`, form);
        showToast("success", "Event updated.");
      } else {
        await postData("admin/calendar-events/create", form);
        showToast("success", "Event added.");
      }
      closeDialog(); load();
    } catch { showToast("error", "Failed to save."); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/calendar-events/${id}`);
      showToast("success", "Event deleted.");
      load();
    } catch { showToast("error", "Failed to delete."); }
  };

  const handleUpdate = () => showToast("success", "Calendar configuration saved.");

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const btnStyle = {
    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
    color: "#fff", fontSize: "13px", textTransform: "none",
    borderRadius: "10px", px: 2.5, py: 1,
    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
  };

  const getTypeStyle = (type) => TYPE_COLORS[type] || TYPE_COLORS.Other;

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Calendar / Program Calendar displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CalendarMonthIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Program Calendar</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage calendar events displayed on the website</Typography>
          </Box>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>+ Calendar</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Title", "Start Date", "End Date", "Type", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No calendar events yet.</TableCell>
                </TableRow>
              ) : pageData.map((row, idx) => {
                const ts = getTypeStyle(row.eventType);
                return (
                  <TableRow key={row._id || row.id || idx}
                    sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                    <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.title}</TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{fmtDate(row.startDate)}</TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#374151" }}>{fmtDate(row.endDate)}</TableCell>
                    <TableCell>
                      <Chip label={row.eventType} size="small"
                        sx={{ backgroundColor: ts.bg, color: ts.color, fontWeight: 600, fontSize: "11px" }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EEF2FF" } }}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page}
              onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button startIcon={<SaveOutlinedIcon />} onClick={handleUpdate} sx={btnStyle}>Update</Button>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>
          {editId ? "Edit Calendar Event" : "Add Calendar Event"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField label="Title" fullWidth size="small" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Start Date" fullWidth size="small" type="date" value={form.startDate}
            onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="End Date" fullWidth size="small" type="date" value={form.endDate}
            onChange={e => setForm(p => ({ ...p, endDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Event Type</InputLabel>
            <Select value={form.eventType} onChange={e => setForm(p => ({ ...p, eventType: e.target.value }))} label="Event Type"
              sx={{ borderRadius: "10px" }}>
              {EVENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Description" fullWidth size="small" multiline rows={3} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminCalendarConfig;
